import React, { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { 
  Zap, 
  Shield, 
  CheckCircle, 
  Sparkles, 
  Mail, 
  Lock, 
  ArrowRight,
  Eye,
  EyeOff,
  Loader2
} from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';
const APP_NAME = "VALIDMAIL";
const SUPPORT_EMAIL = "support@validmail.io";

export default function SignIn({ setAuthToken, setUser, setStatusMessage, pricingCurrency }) {
  // Tab state
  const [activeTab, setActiveTab] = useState("signin"); // signin, otp, forgot

  // Google auth state
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  // Email/Password signin state
  const [signinEmail, setSigninEmail] = useState("");
  const [signinPassword, setSigninPassword] = useState("");
  const [signinLoading, setSigninLoading] = useState(false);
  const [showSigninPassword, setShowSigninPassword] = useState(false);

  // OTP login state
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotOtpSent, setForgotOtpSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // ==================== GOOGLE LOGIN ====================
  const handleGoogleLoginSuccess = async (credentialResponse) => {
    if (!credentialResponse?.credential) {
      setAuthError("Unable to retrieve Google credential.");
      return;
    }
    setAuthLoading(true);
    setAuthError("");
    try {
      const response = await axios.post(`${API}/auth/social-login`, {
        provider: "google",
        token: credentialResponse.credential,
        currency_preference: pricingCurrency || "USD",
      });
      const { access_token: accessToken, user: userData, accessToken: alt } = response.data;
      const tokenToSave = accessToken || alt;
      if (tokenToSave) {
        localStorage.setItem("authToken", tokenToSave);
        setAuthToken?.(tokenToSave);
      }
      setUser?.(userData);
      setStatusMessage?.({ type: "success", text: `Welcome back, ${userData?.name || "user"}!` });
      window.location.href = "/";
    } catch (error) {
      const message = error?.response?.data?.detail || "Google sign-in failed.";
      setAuthError(message);
      setStatusMessage?.({ type: "error", text: message });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogleLoginError = () => {
    setAuthError("Google sign-in was cancelled.");
  };

  // ==================== EMAIL/PASSWORD SIGNIN ====================
  const handleEmailSignin = async (e) => {
    e.preventDefault();
    setAuthError("");
    
    if (!signinEmail || !signinPassword) {
      setAuthError("Please enter both email and password");
      return;
    }

    setSigninLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: signinEmail,
        password: signinPassword,
      });
      const { access_token: accessToken, user: userData } = response.data;
      if (accessToken) {
        localStorage.setItem("authToken", accessToken);
        setAuthToken?.(accessToken);
      }
      setUser?.(userData);
      setStatusMessage?.({ type: "success", text: `Welcome back, ${userData?.name || "user"}!` });
      window.location.href = "/";
    } catch (error) {
      const message = error?.response?.data?.detail || "Login failed. Please check your credentials.";
      setAuthError(message);
      setStatusMessage?.({ type: "error", text: message });
    } finally {
      setSigninLoading(false);
    }
  };

  // ==================== OTP LOGIN ====================
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setAuthError("");
    
    if (!otpEmail) {
      setAuthError("Please enter your email");
      return;
    }

    setOtpLoading(true);
    try {
      await axios.post(`${API}/auth/otp/send`, { email: otpEmail });
      setOtpSent(true);
      setStatusMessage?.({ type: "success", text: "OTP sent to your email!" });
    } catch (error) {
      const message = error?.response?.data?.detail || "Failed to send OTP.";
      setAuthError(message);
      setStatusMessage?.({ type: "error", text: message });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setAuthError("");
    
    if (!otpCode) {
      setAuthError("Please enter the OTP code");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await axios.post(`${API}/auth/otp/verify`, {
        email: otpEmail,
        otp: otpCode,
      });
      const { access_token: accessToken, user: userData } = response.data;
      if (accessToken) {
        localStorage.setItem("authToken", accessToken);
        setAuthToken?.(accessToken);
      }
      setUser?.(userData);
      setStatusMessage?.({ type: "success", text: `Welcome back, ${userData?.name || "user"}!` });
      window.location.href = "/";
    } catch (error) {
      const message = error?.response?.data?.detail || "Invalid OTP. Please try again.";
      setAuthError(message);
      setStatusMessage?.({ type: "error", text: message });
    } finally {
      setOtpLoading(false);
    }
  };

  // ==================== FORGOT PASSWORD ====================
  const handleSendResetOtp = async (e) => {
    e.preventDefault();
    setAuthError("");
    
    if (!forgotEmail) {
      setAuthError("Please enter your email");
      return;
    }

    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotOtpSent(true);
      setStatusMessage?.({ type: "success", text: "Password reset OTP sent to your email!" });
    } catch (error) {
      const message = error?.response?.data?.detail || "Failed to send reset OTP.";
      setAuthError(message);
      setStatusMessage?.({ type: "error", text: message });
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setAuthError("");
    
    if (!forgotOtp || !forgotNewPassword) {
      setAuthError("Please fill in all fields");
      return;
    }

    if (forgotNewPassword.length < 6) {
      setAuthError("Password must be at least 6 characters");
      return;
    }

    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: forgotEmail,
        otp: forgotOtp,
        new_password: forgotNewPassword,
      });
      setStatusMessage?.({ type: "success", text: "Password reset successfully! Please sign in." });
      // Reset form and switch to signin
      setForgotEmail("");
      setForgotOtp("");
      setForgotNewPassword("");
      setForgotOtpSent(false);
      setActiveTab("signin");
    } catch (error) {
      const message = error?.response?.data?.detail || "Failed to reset password.";
      setAuthError(message);
      setStatusMessage?.({ type: "error", text: message });
    } finally {
      setForgotLoading(false);
    }
  };

  // ==================== TAB SWITCHING ====================
  const switchTab = (tab) => {
    setActiveTab(tab);
    setAuthError("");
    if (tab !== "otp") {
      setOtpSent(false);
      setOtpCode("");
    }
    if (tab !== "forgot") {
      setForgotOtpSent(false);
      setForgotOtp("");
      setForgotNewPassword("");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full bg-white shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden grid grid-cols-1 lg:grid-cols-2 border border-gray-200">
        
        {/* LEFT - Company / Marketing */}
        <div className="p-6 sm:p-8 lg:p-12 bg-gradient-to-br from-blue-600 to-purple-600 text-white relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
          </div>

          <div className="relative z-10 flex flex-col h-full justify-between">
            <div>
              {/* Logo & Title */}
              <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="bg-white/20 backdrop-blur p-2.5 sm:p-3 rounded-xl shadow-lg">
                  <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold">{APP_NAME}</h1>
                  <p className="text-xs sm:text-sm text-blue-100">Email Validation Platform</p>
                </div>
              </div>

              {/* Main Heading */}
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 leading-tight">
                Verify emails with confidence
              </h2>
              <p className="text-sm sm:text-base text-blue-100 mb-6 sm:mb-8 leading-relaxed">
                AI-powered email validation with real-time SMTP checks, deliverability scoring, and security analysis.
              </p>

              {/* Features */}
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm sm:text-base">Real-time SMTP Validation</div>
                    <div className="text-xs sm:text-sm text-blue-100">Instant server response verification</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm sm:text-base">AI-Powered Risk Scoring</div>
                    <div className="text-xs sm:text-sm text-blue-100">Machine learning deliverability prediction</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                    <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm sm:text-base">Advanced Security Checks</div>
                    <div className="text-xs sm:text-sm text-blue-100">Detect disposable & risky addresses</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 sm:mt-12">
              <div className="bg-white/10 backdrop-blur rounded-xl p-3 sm:p-4 border border-white/20">
                <p className="text-xs sm:text-sm text-blue-100">
                  ✨ Trusted by developers and businesses worldwide for reliable email validation.
                </p>
              </div>
              <p className="mt-4 text-xs sm:text-sm text-blue-100">
                © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT - Auth Forms */}
        <div className="p-6 sm:p-8 lg:p-12 flex items-center justify-center">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="mb-6 sm:mb-8 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-blue-100 text-blue-700 text-xs sm:text-sm font-medium mb-4">
                <Lock className="w-3 h-3 sm:w-4 sm:h-4" />
                Secure Access
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {activeTab === "otp" ? "Login with OTP" :
                 activeTab === "forgot" ? "Reset Password" :
                 "Welcome back"}
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                {activeTab === "otp" ? "Get a one-time password via email" :
                 activeTab === "forgot" ? "Reset your account password" :
                 "Sign in to continue"}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Don't have an account?{" "}
                <Link to={"/signup"} className="text-blue-600 hover:text-blue-700 font-semibold">
                  Sign up
                </Link>
              </p>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto">
              <button
                onClick={() => switchTab("signin")}
                className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === "signin"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => switchTab("otp")}
                className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === "otp"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                OTP Login
              </button>
            </div>

            {/* Auth Card */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-sm">
              {authError && (
                <div className="mb-4 sm:mb-6 text-sm sm:text-base text-red-700 bg-red-50 border-2 border-red-200 p-3 sm:p-4 rounded-lg flex items-start gap-2">
                  <span className="flex-shrink-0 text-lg">⚠️</span>
                  <span>{authError}</span>
                </div>
              )}

              {/* SIGN IN TAB */}
              {activeTab === "signin" && (
                <div className="space-y-4 sm:space-y-6">
                  <form onSubmit={handleEmailSignin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          value={signinEmail}
                          onChange={(e) => setSigninEmail(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="you@example.com"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type={showSigninPassword ? "text" : "password"}
                          value={signinPassword}
                          onChange={(e) => setSigninPassword(e.target.value)}
                          className="w-full pl-10 pr-12 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="••••••••"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowSigninPassword(!showSigninPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSigninPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => switchTab("forgot")}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Forgot password?
                    </button>

                    <button
                      type="submit"
                      disabled={signinLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {signinLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        <>
                          Sign In
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </form>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-xs sm:text-sm">
                      <span className="px-2 sm:px-4 bg-gray-50 text-gray-500">Or continue with</span>
                    </div>
                  </div>

                  {/* Google Login */}
                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={handleGoogleLoginSuccess}
                      onError={handleGoogleLoginError}
                      useOneTap={false}
                      size="large"
                      width="100%"
                    />
                  </div>

                  {authLoading && (
                    <div className="flex flex-col items-center justify-center py-4 gap-3">
                      <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
                      <p className="text-sm text-gray-600 font-medium">Signing you in...</p>
                    </div>
                  )}
                </div>
              )}

              {/* OTP LOGIN TAB */}
              {activeTab === "otp" && (
                <div className="space-y-4 sm:space-y-6">
                  {!otpSent ? (
                    <form onSubmit={handleSendOtp} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="email"
                            value={otpEmail}
                            onChange={(e) => setOtpEmail(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder="you@example.com"
                            required
                          />
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          We'll send a one-time password to your email
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={otpLoading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {otpLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Sending OTP...
                          </>
                        ) : (
                          <>
                            Send OTP
                            <ArrowRight className="w-5 h-5" />
                          </>
                        )}
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleVerifyOtp} className="space-y-4">
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-700">
                          ✉️ OTP sent to <strong>{otpEmail}</strong>
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enter OTP Code
                        </label>
                        <input
                          type="text"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center text-2xl tracking-widest"
                          placeholder="000000"
                          maxLength={6}
                          required
                        />
                        <p className="mt-2 text-xs text-gray-500">
                          Check your email for the 6-digit code
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={otpLoading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {otpLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          <>
                            Verify & Sign In
                            <ArrowRight className="w-5 h-5" />
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setOtpSent(false);
                          setOtpCode("");
                        }}
                        className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        ← Change email address
                      </button>
                    </form>
                  )}
                </div>
              )}

              {/* FORGOT PASSWORD TAB */}
              {activeTab === "forgot" && (
                <div className="space-y-4 sm:space-y-6">
                  {!forgotOtpSent ? (
                    <form onSubmit={handleSendResetOtp} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="email"
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder="you@example.com"
                            required
                          />
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          We'll send a password reset code to your email
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={forgotLoading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {forgotLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            Send Reset Code
                            <ArrowRight className="w-5 h-5" />
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => switchTab("signin")}
                        className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        ← Back to sign in
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleResetPassword} className="space-y-4">
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-700">
                          ✉️ Reset code sent to <strong>{forgotEmail}</strong>
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enter Reset Code
                        </label>
                        <input
                          type="text"
                          value={forgotOtp}
                          onChange={(e) => setForgotOtp(e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center text-2xl tracking-widest"
                          placeholder="000000"
                          maxLength={6}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type={showForgotPassword ? "text" : "password"}
                            value={forgotNewPassword}
                            onChange={(e) => setForgotNewPassword(e.target.value)}
                            className="w-full pl-10 pr-12 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder="••••••••"
                            required
                            minLength={6}
                          />
                          <button
                            type="button"
                            onClick={() => setShowForgotPassword(!showForgotPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showForgotPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">Minimum 6 characters</p>
                      </div>

                      <button
                        type="submit"
                        disabled={forgotLoading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {forgotLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Resetting...
                          </>
                        ) : (
                          <>
                            Reset Password
                            <ArrowRight className="w-5 h-5" />
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setForgotOtpSent(false);
                          setForgotOtp("");
                          setForgotNewPassword("");
                        }}
                        className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        ← Change email address
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>

            {/* Help */}
            <div className="mt-6 sm:mt-8 text-center">
              <p className="text-xs sm:text-sm text-gray-600">
                Need help?{" "}
                <a 
                  href={`mailto:${SUPPORT_EMAIL}`} 
                  className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
                >
                  Contact Support <ArrowRight className="w-3 h-3" />
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
